import React, { useState, useEffect } from "react";

import { Link, useNavigate } from "react-router-dom";
import { useMainPageTitle } from "../../contexts/PageContext";
import { useAuth } from "../../contexts/authContext";
import Btn from "../../components/form/btn";

const API_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1";

export default function CompleteProfileDocuments() {
  const navigate = useNavigate();
  const { updatePageTitle } = useMainPageTitle();
  const { user } = useAuth();


  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [dragActive, setDragActive] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  useEffect(() => {
    updatePageTitle("Complete Profile - Documents", "Upload your documents");

    console.log("📄 Documents page loaded, user:", user);

    if (!user) {
      console.log("📄 No user, redirecting to signin");
      navigate("/signin");
      return;
    }

    // Validate required previous steps based on account type
    if (!user.accountType || user.accountType.trim() === "") {
      navigate("/complete-profile");
      return;
    }
    if (!user.phone || user.phone.trim() === "") {
      navigate("/complete-profile/phone");
      return;
    }

    // Non-organizations need gender
    if (
      user.accountType !== "organization" &&
      (!user.gender || user.gender.trim() === "")
    ) {
      navigate("/complete-profile/gender");
      return;
    }

    // Players need to complete birthday step (even if skipped)
    if (user.accountType === "player" && !user.metaData?.hasCompletedBirthday) {
      console.log("📄 Player hasn't completed birthday, redirecting back");
      navigate("/complete-profile/birthday");
      return;
    }

    console.log("📄 All validations passed, showing documents page");
  }, [user, navigate, updatePageTitle]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = (files) => {
    const fileArray = Array.from(files);
    const validFiles = fileArray.filter((file) => {
      // Basic file validation
      const maxSize = 10 * 1024 * 1024; // 10MB
      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/jpg",
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];

      if (file.size > maxSize) {
        setErrors((prev) => ({
          ...prev,
          files: `File ${file.name} is too large. Maximum size is 10MB.`,
        }));
        return false;
      }

      if (!allowedTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          files: `File ${file.name} has an unsupported format.`,
        }));
        return false;
      }

      return true;
    });

    setSelectedFiles((prev) => [...prev, ...validFiles]);
    setErrors((prev) => ({ ...prev, files: "" }));
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDocumentsSubmit = async () => {
    setIsLoading(true);
    setErrors({});
    setUploadSuccess(false);

    try {
      const token = localStorage.getItem("token");
      const newUploadedFiles = [];

      // Documents are optional, so we can submit even if no files
      if (selectedFiles.length > 0) {
        for (const file of selectedFiles) {
          const formData = new FormData();
          formData.append("userDocument", file);

          const response = await fetch(`${API_URL}/user/add-user-document`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          });

          if (!response.ok) {
            throw new Error(`Failed to upload ${file.name}`);
          }

          const responseData = await response.json();

          // Add the uploaded file info to our tracking
          if (
            responseData.userDocument &&
            responseData.userDocument.length > 0
          ) {
            const latestDoc =
              responseData.userDocument[responseData.userDocument.length - 1];
            newUploadedFiles.push({
              name: latestDoc.name,
              size: latestDoc.size,
              type: latestDoc.type,
              uploadedAt: latestDoc.createdAt,
              originalFile: file,
            });
          }
        }

        // Update uploaded files and clear selected files
        setUploadedFiles((prev) => [...prev, ...newUploadedFiles]);
        setSelectedFiles([]);
        setUploadSuccess(true);

        // Auto-hide success message after 3 seconds
        setTimeout(() => setUploadSuccess(false), 3000);
      }

      // If no files to upload, just navigate
      if (selectedFiles.length === 0) {
        // Navigate based on user type
        if (user.accountType === "organization") {
          navigate("/organization/dashboard");
        } else {
          navigate("/feed");
        }
      }
    } catch (error) {
      setErrors({ documents: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleSkip = () => {
    // Navigate based on user type
    if (user.accountType === "organization") {
      navigate("/organization/dashboard");
    } else {
      navigate("/feed");
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Progress Indicator */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center bg-white rounded-full px-6 py-3 shadow-lg">
            <div className="flex items-center space-x-2 opacity-75">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                ✓
              </div>
              <span className="text-gray-600 font-medium">Account Type</span>
            </div>
            <div className="mx-4 w-8 h-0.5 bg-green-500"></div>
            <div className="flex items-center space-x-2 opacity-75">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                ✓
              </div>
              <span className="text-gray-600 font-medium">Phone</span>
            </div>
            <div className="mx-4 w-8 h-0.5 bg-green-500"></div>
            <div className="flex items-center space-x-2 opacity-75">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                ✓
              </div>
              <span className="text-gray-600 font-medium">Details</span>
            </div>
            <div className="mx-4 w-8 h-0.5 bg-green-500"></div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-[#F15213] rounded-full flex items-center justify-center text-white text-sm font-bold">
                4
              </div>
              <span className="text-gray-600 font-medium">Documents</span>
            </div>
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-[#F15213] rounded-full flex items-center justify-center mx-auto mb-6">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 384 512"
                className="w-10 h-10 text-white"
              >
                <path
                  fill="currentColor"
                  d="M64 0C28.7 0 0 28.7 0 64V448c0 35.3 28.7 64 64 64H320c35.3 0 64-28.7 64-64V160H256c-17.7 0-32-14.3-32-32V0H64zM256 0V128H384L256 0zM112 256H272c8.8 0 16 7.2 16 16s-7.2 16-16 16H112c-8.8 0-16-7.2-16-16s7.2-16 16-16zm0 64H272c8.8 0 16 7.2 16 16s-7.2 16-16 16H112c-8.8 0-16-7.2-16-16s7.2-16 16-16zm0 64H272c8.8 0 16 7.2 16 16s-7.2 16-16 16H112c-8.8 0-16-7.2-16-16s7.2-16 16-16z"
                />
              </svg>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#F15213] mb-4">
              Upload Documents
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Share any relevant documents to complete your profile. This step
              is optional.
            </p>
          </div>

          {errors.documents && (
            <div className="bg-red-50 border-l-4 border-red-400 text-red-700 px-6 py-4 rounded-lg mb-8">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-red-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm">{errors.documents}</p>
                </div>
              </div>
            </div>
          )}

          {errors.files && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 text-yellow-700 px-6 py-4 rounded-lg mb-8">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-yellow-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm">{errors.files}</p>
                </div>
              </div>
            </div>
          )}

          {uploadSuccess && (
            <div className="bg-green-50 border-l-4 border-green-400 text-green-700 px-6 py-4 rounded-lg mb-8">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-green-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium">
                    Files uploaded successfully! 🎉
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* File Upload Area */}
          <div className="mb-12">
            <div
              className={`relative border-3 border-dashed rounded-3xl p-12 text-center transition-all duration-300 ${
                dragActive
                  ? "border-[#F15213] bg-orange-50"
                  : "border-gray-300 hover:border-gray-400 hover:bg-gray-50"
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                type="file"
                multiple
                onChange={handleFileSelect}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
              />

              <div className="space-y-6">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"
                    className="w-8 h-8 text-[#F15213]"
                  >
                    <path
                      fill="currentColor"
                      d="M288 109.3V352c0 17.7-14.3 32-32 32s-32-14.3-32-32V109.3l-73.4 73.4c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3l128-128c12.5-12.5 32.8-12.5 45.3 0l128 128c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L288 109.3zM64 352H192c0 35.3 28.7 64 64 64s64-28.7 64-64H448c35.3 0 64 28.7 64 64v32c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V416c0-35.3 28.7-64 64-64zM432 456a24 24 0 1 0 0-48 24 24 0 1 0 0 48z"
                    />
                  </svg>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">
                    Drop files here or click to browse
                  </h3>
                  <p className="text-gray-600 text-lg">
                    Supported formats: JPG, PNG, PDF, DOC, DOCX (Max 10MB each)
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Uploaded Files */}
          {uploadedFiles.length > 0 && (
            <div className="mb-12">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <svg
                  className="w-6 h-6 text-green-500 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Uploaded Documents ({uploadedFiles.length})
              </h3>
              <div className="space-y-3">
                {uploadedFiles.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl p-4"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 384 512"
                          className="w-5 h-5 text-green-600"
                        >
                          <path
                            fill="currentColor"
                            d="M64 0C28.7 0 0 28.7 0 64V448c0 35.3 28.7 64 64 64H320c35.3 0 64-28.7 64-64V160H256c-17.7 0-32-14.3-32-32V0H64zM256 0V128H384L256 0z"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">{file.name}</p>
                        <p className="text-sm text-gray-500">
                          {(file.size / 1024 / 1024).toFixed(2)} MB • Uploaded
                          successfully
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                        <svg
                          className="w-5 h-5 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Selected Files */}
          {selectedFiles.length > 0 && (
            <div className="mb-12">
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                Ready to Upload:
              </h3>
              <div className="space-y-3">
                {selectedFiles.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-xl p-4"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 384 512"
                          className="w-5 h-5 text-blue-600"
                        >
                          <path
                            fill="currentColor"
                            d="M64 0C28.7 0 0 28.7 0 64V448c0 35.3 28.7 64 64 64H320c35.3 0 64-28.7 64-64V160H256c-17.7 0-32-14.3-32-32V0H64zM256 0V128H384L256 0z"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">{file.name}</p>
                        <p className="text-sm text-gray-500">
                          {(file.size / 1024 / 1024).toFixed(2)} MB • Ready to
                          upload
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => removeFile(index)}
                      className="text-red-500 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-colors"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        className="w-5 h-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            {/* Show different buttons based on state */}
            {uploadedFiles.length > 0 && selectedFiles.length === 0 ? (
              // When files are uploaded and no new files selected - show finish button
              <>
                <button
                  onClick={() => setUploadSuccess(false)}
                  className="px-8 py-3 text-lg font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-300 hover:scale-105"
                >
                  Add More Files
                </button>

                <Link
                  to="/user/profile"
                  className="px-12 py-4 text-xl font-semibold bg-green-600 hover:bg-green-700 transform hover:scale-105 transition-all duration-300 shadow-xl hover:shadow-2xl"
                >
                  <>
                    Finish & Continue
                    <svg
                      className="ml-2 w-5 h-5 inline"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </>
                </Link>
              </>
            ) : (
              // Default state - show upload/skip buttons
              <>
                <Link
                  to="/user/profile"
                  className="px-8 py-3 text-lg font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-300 hover:scale-105"
                >
                  Skip for now
                </Link>

                <Btn
                  onClick={handleDocumentsSubmit}
                  disabled={isLoading || selectedFiles.length === 0}
                  className="px-12 py-4 text-xl font-semibold bg-[#F15213] hover:bg-orange-600 transform hover:scale-105 transition-all duration-300 shadow-xl hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <svg
                        className="animate-spin -ml-1 mr-3 h-6 w-6 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Uploading...
                    </div>
                  ) : (
                    <>
                      Upload Files ({selectedFiles.length})
                      <svg
                        className="ml-2 w-5 h-5 inline"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 16l-4-4m0 0l4-4m-4 4h18"
                        />
                      </svg>
                    </>
                  )}
                </Btn>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
